import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { AppContext } from 'Context/AppContext';
import Badge from 'Components/DesignSystem/Badge/Badge';
import Button from 'Components/DesignSystem/Button/Button';
import Divider from 'Components/DesignSystem/Divider/Divider';
import EmptyState from 'Components/DesignSystem/EmptyState/EmptyState';
import ErrorState from 'Components/DesignSystem/ErrorState/ErrorState';
import Tab from 'Components/DesignSystem/Tab/Tab';
import Table from 'Components/DesignSystem/Table/Table';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { Edit } from 'Components/DesignSystem/Iconography/Icons';
import { TableColumn } from 'types/DesignSystem/Table';
import {
  ADAPTERS_BASE_ID,
  ADAPTERS_COPY,
  ADAPTERS_READ_PERMISSION,
  ADAPTERS_UPDATE_PERMISSION,
  FEED_TABS,
  feedLabel,
} from './Constants';
import { AdaptersProvider, useAdapters } from './Context/AdaptersContext';
import PartiesCell from './PartiesCell';
import { countAssignedParties } from './assignmentState';
import { AdapterRow, FeedType } from './types';

const BASE = ADAPTERS_BASE_ID;

const columns: TableColumn<AdapterRow>[] = [
  {
    id: 'adapter',
    header: 'Adapter',
    accessor: 'name',
    width: '24%',
    render: (_value, row) => (
      <Typography variant="title-header" as="span" className="tw-text-ink" data-testid={`${BASE}-row-${row.key}-name`}>
        {row.name}
      </Typography>
    ),
  },
  {
    id: 'description',
    header: 'Description',
    accessor: 'description',
    width: '36%',
    render: (_value, row) => (
      <Typography
        variant="body-primary"
        as="span"
        className="tw-text-ink-secondary"
        data-testid={`${BASE}-row-${row.key}-description`}
      >
        {row.description}
      </Typography>
    ),
  },
  {
    id: 'parties',
    header: 'Parties',
    accessor: 'key',
    cellType: 'custom',
    render: (_value, row) => <PartiesCell adapter={row} />,
  },
];

const AdaptersPage = ({ canEdit }: { canEdit: boolean }) => {
  const {
    isLoading, loadError, adapters, parties, feedTypes, current,
    isEditMode, isDirty, isSaving, reload, startEdit, cancelEdit, save,
  } = useAdapters();
  const [activeFeed, setActiveFeed] = useState<FeedType>(FEED_TABS[0].id);

  const tabs = useMemo(
    () =>
      FEED_TABS.filter((tab) => feedTypes.includes(tab.id)).map((tab) => ({
        id: tab.id,
        label: tab.label,
        'data-testid': `${BASE}-tab-${tab.id}`,
        htmlId: `${BASE}-tab-${tab.id}`,
      })),
    [feedTypes],
  );

  const rows = useMemo(() => adapters.filter((adapter) => adapter.feed_type === activeFeed), [adapters, activeFeed]);
  const label = feedLabel(activeFeed);
  const assignedCount = countAssignedParties(current);

  let body: React.ReactNode;
  if (loadError) {
    body = (
      <ErrorState
        title={ADAPTERS_COPY.loadFailedTitle}
        description={loadError}
        size="md"
        action={
          <Button variant="secondary" size="sm" onClick={() => { reload(); }} data-testid={`${BASE}-retry-btn`}>
            {ADAPTERS_COPY.retry}
          </Button>
        }
        data-testid={`${BASE}-error`}
      />
    );
  } else if (!isLoading && rows.length === 0) {
    body = (
      <EmptyState
        size="md"
        title={ADAPTERS_COPY.emptyTitle(label)}
        description={ADAPTERS_COPY.emptyDescription}
        data-testid={`${BASE}-empty-${activeFeed}`}
      />
    );
  } else {
    body = (
      <div data-testid={isLoading ? `${BASE}-loading` : `${BASE}-table`}>
        <Table<AdapterRow>
          columns={columns}
          data={rows}
          rowKey="key"
          loading={isLoading}
          density="comfortable"
          hoverable={false}
        />
      </div>
    );
  }

  return (
    <div data-testid={`${BASE}-page`}>
      <div className="tw-py-2">
        <Typography variant="display" as="h1" className="tw-text-ink" data-testid={`${BASE}-title`}>
          {ADAPTERS_COPY.title}
        </Typography>
      </div>
      <Divider />

      <div className="tw-flex tw-flex-wrap tw-items-end tw-justify-between tw-gap-4 tw-mt-2">
        <div className="tw-flex-1 tw-min-w-0">
          <Tab
            tabs={tabs}
            activeTabId={activeFeed}
            onChange={(tabId) => setActiveFeed(String(tabId) as FeedType)}
            data-testid={`${BASE}-tabs`}
          />
        </div>
        <div className="tw-flex tw-items-center tw-gap-2 tw-pb-1" data-testid={`${BASE}-actions`}>
          {isEditMode ? (
            <>
              {isDirty && (
                <Badge variant="warning" size="sm" data-testid={`${BASE}-unsaved`}>
                  {ADAPTERS_COPY.unsaved}
                </Badge>
              )}
              <Button variant="secondary" size="sm" onClick={cancelEdit} disabled={isSaving} data-testid={`${BASE}-cancel-btn`}>
                {ADAPTERS_COPY.cancel}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => { save(); }}
                disabled={!isDirty || isSaving}
                loading={isSaving}
                data-testid={`${BASE}-save-btn`}
              >
                {ADAPTERS_COPY.save}
              </Button>
            </>
          ) : (
            canEdit && !loadError && (
              <Button
                variant="primary"
                size="sm"
                showIcon
                icon={<Edit size={16} />}
                onClick={startEdit}
                disabled={isLoading}
                data-testid={`${BASE}-edit-btn`}
              >
                {ADAPTERS_COPY.edit}
              </Button>
            )
          )}
        </div>
      </div>

      <div className="tw-mt-3">{body}</div>

      {!loadError && (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-mt-3">
          <Typography variant="caption-metadata" as="span" className="tw-text-ink-secondary" data-testid={`${BASE}-footer`}>
            {ADAPTERS_COPY.footer(rows.length, label, assignedCount, parties.length)}
          </Typography>
          <Typography variant="caption-metadata" as="span" className="tw-text-ink-secondary" data-testid={`${BASE}-footnote`}>
            {isEditMode ? ADAPTERS_COPY.footnoteEdit : ADAPTERS_COPY.footnoteView}
          </Typography>
        </div>
      )}
    </div>
  );
};

/**
 * `/admin/adapters` — Compatio Admin only (AB#14369).
 *
 * The sidebar already hides the item without `gmsadapter_read` (`MenuItem.tsx`); this mount
 * check covers direct URL access the same way `/admin/settings` does, redirecting to the
 * permission-denied route and rendering nothing. `gmsadapter_update` decides whether the Edit
 * button exists — a read-only holder sees the table and nothing else.
 */
const AdaptersView = () => {
  const appData = useContext(AppContext);
  const navigate = useNavigate();
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    if (!appData.pm.checkPermission(ADAPTERS_READ_PERMISSION)) {
      navigate('/workspace/module/permission/denied');
    } else {
      setAllowed(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!allowed) return null;

  return (
    <AdaptersProvider>
      <AdaptersPage canEdit={Boolean(appData.pm.checkPermission(ADAPTERS_UPDATE_PERMISSION))} />
    </AdaptersProvider>
  );
};

export default AdaptersView;
